import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Settings, User, Key, Bell, Shield, Save, Loader2, Phone, CheckCircle, CreditCard, Zap, Crown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const PLANS = [
  { id: 'creator', name: 'Creator', price: '$9/month', credits: '200,000 chars', color: 'from-blue-500 to-cyan-500' },
  { id: 'pro', name: 'Pro', price: '$29/month', credits: '1,000,000 chars', color: 'from-purple-500 to-pink-500' },
];

const SettingsPage = () => {
  const { user, token, API, refreshUser } = useAuth();
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [upgrading, setUpgrading] = useState('');

  useEffect(() => {
    if (user) {
      setName(user.name || '');
      setPhone(user.phone || '');
    }
  }, [user]);

  // Check for payment session on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (sessionId && token) {
      axios.get(`${API}/payments/status/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(r => {
        if (r.data.payment_status === 'paid') {
          toast.success('Payment successful! Your plan has been upgraded.');
          refreshUser?.();
        }
      }).catch(() => {});
    }
  }, [token, API, refreshUser]);

  const handleSaveProfile = async () => {
    if (!name.trim()) { toast.error('Name cannot be empty'); return; }
    setSaving(true);
    setSaved(false);
    try {
      await axios.put(`${API}/auth/profile`,
        { name: name.trim(), phone: phone.trim() },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await refreshUser?.();
      setSaved(true);
      toast.success('Profile updated!');
      setTimeout(() => setSaved(false), 3000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleUpgrade = async (planId) => {
    setUpgrading(planId);
    try {
      const res = await axios.post(`${API}/payments/checkout?plan_id=${planId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setUpgrading('');
    }
  };

  const hasChanges = name !== (user?.name || '') || phone !== (user?.phone || '');

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <div className="glass-card rounded-2xl p-6 border border-border">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-slate-500 to-slate-700 flex items-center justify-center">
            <Settings className="w-5 h-5 text-white" />
          </div>
          <h2 className="font-bold font-outfit text-foreground text-lg">Settings</h2>
        </div>

        {/* Profile */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-widest flex items-center gap-2">
            <User className="w-4 h-4 text-primary" /> Profile
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Full Name</label>
              <input data-testid="settings-name" type="text" value={name} onChange={e => setName(e.target.value)}
                className="w-full px-4 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="Your name" />
            </div>
            <div>
              <label className="block text-xs text-muted-foreground mb-1">Email</label>
              <input data-testid="settings-email" type="email" defaultValue={user?.email} readOnly
                className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm cursor-not-allowed opacity-70" />
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1 flex items-center gap-1"><Phone className="w-3 h-3" /> Phone Number</label>
            <input data-testid="settings-phone" type="tel" value={phone} onChange={e => setPhone(e.target.value)}
              className="w-full px-4 py-2.5 rounded-xl bg-background border border-border text-foreground text-sm focus:border-primary focus:ring-1 focus:ring-primary transition-all" placeholder="+1 (555) 123-4567" />
          </div>
          <button data-testid="save-profile-btn" onClick={handleSaveProfile} disabled={saving || !hasChanges}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl gradient-btn text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed transition-all">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving...</> : saved ? <><CheckCircle className="w-4 h-4" /> Saved!</> : <><Save className="w-4 h-4" /> Save Changes</>}
          </button>
        </div>

        <div className="border-t border-border my-6" />

        {/* Plan & Usage */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-widest flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" /> Plan & Usage
          </h3>
          <div className="p-5 rounded-xl bg-gradient-to-r from-blue-500/10 to-purple-500/10 border border-primary/20">
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="font-semibold text-foreground capitalize">{user?.plan || 'Free'} Plan</p>
                <p className="text-sm text-muted-foreground">{(user?.credits || 10000).toLocaleString()} total characters</p>
              </div>
              <span className="px-3 py-1 rounded-full bg-primary/20 text-primary text-xs font-semibold capitalize">{user?.plan || 'FREE'}</span>
            </div>
            <div className="h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full" style={{ width: `${Math.min(100, ((user?.credits_used || 0) / (user?.credits || 10000)) * 100)}%` }} />
            </div>
            <div className="flex justify-between text-xs text-muted-foreground mt-1.5">
              <span>{(user?.credits_used || 0).toLocaleString()} used</span>
              <span>{Math.max(0, (user?.credits || 10000) - (user?.credits_used || 0)).toLocaleString()} remaining</span>
            </div>
          </div>

          {/* Upgrade Options */}
          {(!user?.plan || user?.plan === 'free') && (
            <div className="space-y-3">
              <p className="text-sm text-muted-foreground">Upgrade to unlock more characters and features:</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {PLANS.map(plan => (
                  <div key={plan.id} className="rounded-xl border border-border p-4 hover:border-primary/30 transition-all">
                    <div className="flex items-center gap-2 mb-2">
                      <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center`}>
                        {plan.id === 'pro' ? <Crown className="w-4 h-4 text-white" /> : <Zap className="w-4 h-4 text-white" />}
                      </div>
                      <div>
                        <p className="font-semibold text-foreground text-sm">{plan.name}</p>
                        <p className="text-xs text-muted-foreground">{plan.price} · {plan.credits}</p>
                      </div>
                    </div>
                    <button
                      data-testid={`upgrade-${plan.id}-btn`}
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={!!upgrading}
                      className="w-full mt-2 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center justify-center gap-1.5"
                    >
                      {upgrading === plan.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />}
                      {upgrading === plan.id ? 'Redirecting...' : `Upgrade to ${plan.name}`}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border-t border-border my-6" />

        {/* API Config */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-widest flex items-center gap-2">
            <Key className="w-4 h-4 text-primary" /> API Configuration
          </h3>
          <div className="p-4 rounded-xl bg-secondary/50 border border-border">
            <p className="text-sm text-muted-foreground mb-2">AI Voice Engine Status</p>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
              <span className="text-sm text-green-500 font-medium">Connected — Neural AI Engine Active</span>
            </div>
          </div>
        </div>

        <div className="border-t border-border my-6" />
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-foreground uppercase tracking-widest flex items-center gap-2">
            <Bell className="w-4 h-4 text-primary" /> Notifications
          </h3>
          <div className="p-3 rounded-xl bg-secondary/50 border border-border">
            <p className="text-xs text-muted-foreground">Notification preferences coming in next update.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
